// @UI
import { useEffect, useState } from 'react'

import { BiFullscreen } from 'react-icons/bi'
import { FaPause, FaPlay } from 'react-icons/fa'
import { IoPlayBack, IoPlayForward } from 'react-icons/io5'

import {
  Box,
  Button,
  Circle,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  VStack,
} from '@chakra-ui/react'
import styled from '@emotion/styled'
import screenfull from 'screenfull'

import { MediaToggle } from '@/components/ui'

import { SeekRow } from './SeekRow'

interface IProps {
  onSeek: (newValue: number) => void
  onRewind: () => void
  onPlayPause: () => void
  onFastForward: () => void
  isPlaying: boolean
  played: number
  onToggleFullScreen: () => void
  showToggleLiveButton?: boolean
  liveEnabled?: boolean
  onToggleLive?: (boolean) => void
  liveOnly?: boolean
  hideSkipButtons?: boolean
  /** When true, clip button shows loading state */
  isClipLoading?: boolean
  /** Label for clip button when not loading */
  clipLabel?: string
  /** Called when the user clicks the Clip toggle */
  onClipClick?: () => void
  /** Called when the user clicks the Extended toggle */
  onExtendedClick?: () => void
  /** Whether the Extended toggle is the active selection */
  isExtendedActive?: boolean
  /** Whether an EXTENDED clip is available (shows the Extended toggle) */
  hasExtendedClip?: boolean
  /** Current playback position in seconds */
  currentTime?: number
  /** Total video duration in seconds */
  duration?: number
  /** Controls overlay visibility */
  isVisible?: boolean
}

interface VideoControlButtonIProps {
  ariaLabel: string
  handleOnClick: () => void
  children: string
}

interface VideoControlIconButtonIProps {
  ariaLabel: string
  handleOnClick: () => void
  icon: React.ReactElement
  [key: string]: unknown
}

const Wrapper = styled.div<{ $visible: boolean }>`
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  display: flex;
  align-items: flex-end;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 150px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%);
`

const VideoControlButton = ({
  ariaLabel,
  handleOnClick,
  children,
}: VideoControlButtonIProps) => {
  return (
    <Button
      _active={{
        bg: 'rgb(226 232 240 / 20%)',
      }}
      _focus={{
        bg: 'rgb(226 232 240 / 20%)',
      }}
      _hover={{
        bg: '',
      }}
      aria-label={ariaLabel}
      borderRadius='full'
      color='white'
      fontSize='12px'
      fontWeight='normal'
      onClick={(e) => {
        e.stopPropagation()
        handleOnClick()
      }}
      px='0'
      size='sm'
      variant='ghost'
    >
      {children}
    </Button>
  )
}

const VideoControlIconButton = ({
  ariaLabel,
  icon,
  handleOnClick,
  ...rest
}: VideoControlIconButtonIProps) => {
  return (
    <IconButton
      _active={{
        bg: 'rgb(226 232 240 / 20%)',
      }}
      _focus={{
        bg: 'rgb(226 232 240 / 20%)',
      }}
      _hover={{
        bg: '',
      }}
      aria-label={ariaLabel}
      borderRadius='full'
      color='white'
      icon={icon}
      onClick={(e) => {
        e.stopPropagation()
        handleOnClick()
      }}
      variant='ghost'
      {...rest}
    />
  )
}

export const VideoControls = ({
  liveOnly,
  onSeek,
  onRewind,
  onPlayPause,
  onFastForward,
  isPlaying,
  played,
  onToggleFullScreen,
  onToggleLive,
  liveEnabled = false,
  showToggleLiveButton = true,
  hideSkipButtons = false,
  isClipLoading = false,
  clipLabel,
  onClipClick,
  onExtendedClick,
  isExtendedActive = false,
  hasExtendedClip = false,
  currentTime = 0,
  duration = 0,
  isVisible = false,
}: IProps) => {
  const [isLive, setIsLive] = useState(liveEnabled)

  useEffect(() => {
    if (onToggleLive) {
      onToggleLive(isLive)
    }
  }, [isLive, onToggleLive])

  return (
    <Wrapper $visible={isVisible}>
      <Box pb='20px' px='15px' w='full'>
        <VStack align='stretch' spacing='16px'>
          {!isLive && (
            <SeekRow
              currentTime={currentTime}
              duration={duration}
              onSeek={onSeek}
              played={played}
            />
          )}

          <Grid gap={2} templateColumns='repeat(3, 1fr)'>
            <GridItem alignSelf='center'>
              {showToggleLiveButton ? (
                !liveOnly ? (
                  <MediaToggle
                    clipLabel={clipLabel}
                    isClipActive={
                      !isLive && !isClipLoading && !isExtendedActive
                    }
                    isClipLoading={isClipLoading}
                    isExtendedActive={!isLive && isExtendedActive}
                    isLiveActive={isLive}
                    onClipClick={() => {
                      setIsLive(false)
                      onClipClick?.()
                    }}
                    onExtendedClick={() => {
                      setIsLive(false)
                      onExtendedClick?.()
                    }}
                    onLiveClick={() => setIsLive(true)}
                    showExtended={hasExtendedClip}
                  />
                ) : (
                  <Flex alignItems='center'>
                    <Circle
                      bg={isPlaying ? 'alert.100' : 'brand.600'}
                      mr='2'
                      size='8px'
                      transition='ease-in-out'
                      transitionDuration='300ms'
                      transitionProperty='background-color'
                    />
                    <Box color='white' fontSize='12px' fontWeight='semibold'>
                      LIVE
                    </Box>
                  </Flex>
                )
              ) : null}
            </GridItem>
            <GridItem justifySelf='center'>
              <HStack spacing='5px'>
                {!liveOnly && !hideSkipButtons && (
                  <>
                    <VideoControlButton
                      ariaLabel='skip back 10 seconds.'
                      handleOnClick={onRewind}
                    >
                      10s
                    </VideoControlButton>
                    <VideoControlIconButton
                      ariaLabel='skip back 10 seconds.'
                      handleOnClick={onRewind}
                      icon={<Icon as={IoPlayBack} />}
                      size='sm'
                    />
                  </>
                )}

                <VideoControlIconButton
                  ariaLabel='play video'
                  borderWidth='2px'
                  h='40px'
                  handleOnClick={onPlayPause}
                  icon={<Icon as={isPlaying ? FaPause : FaPlay} />}
                  w='40px'
                />
                {!liveOnly && !hideSkipButtons && (
                  <>
                    <VideoControlIconButton
                      ariaLabel='skip forward 10 seconds.'
                      handleOnClick={onFastForward}
                      icon={<Icon as={IoPlayForward} />}
                      size='sm'
                    />
                    <VideoControlButton
                      ariaLabel='skip forward 10 seconds.'
                      handleOnClick={onFastForward}
                    >
                      10s
                    </VideoControlButton>
                  </>
                )}
              </HStack>
            </GridItem>
            {screenfull.isEnabled && (
              <GridItem alignSelf='center' justifySelf='end'>
                <VideoControlIconButton
                  ariaLabel='toggle full screen view.'
                  handleOnClick={onToggleFullScreen}
                  icon={<Icon as={BiFullscreen} boxSize='24px' />}
                  size='sm'
                />
              </GridItem>
            )}
          </Grid>
        </VStack>
      </Box>
    </Wrapper>
  )
}
